// src/component/MusicPrefectureMap.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GoogleMap, CircleF, useJsApiLoader } from "@react-google-maps/api";

const PREF_COORDS = {
  北海道: { lat: 43.06417, lng: 141.34694 },
  青森県: { lat: 40.82444, lng: 140.74 },
  岩手県: { lat: 39.70361, lng: 141.1525 },
  宮城県: { lat: 38.26889, lng: 140.87194 },
  秋田県: { lat: 39.71861, lng: 140.1025 },
  山形県: { lat: 38.24056, lng: 140.36333 },
  福島県: { lat: 37.75, lng: 140.46778 },
  茨城県: { lat: 36.34139, lng: 140.44667 },
  栃木県: { lat: 36.56583, lng: 139.88361 },
  群馬県: { lat: 36.39111, lng: 139.06083 },
  埼玉県: { lat: 35.85694, lng: 139.64889 },
  千葉県: { lat: 35.60472, lng: 140.12333 },
  東京都: { lat: 35.68944, lng: 139.69167 },
  神奈川県: { lat: 35.44778, lng: 139.6425 },
  新潟県: { lat: 37.90222, lng: 139.02361 },
  富山県: { lat: 36.69528, lng: 137.21139 },
  石川県: { lat: 36.59444, lng: 136.62556 },
  福井県: { lat: 36.06528, lng: 136.22194 },
  山梨県: { lat: 35.66389, lng: 138.56833 },
  長野県: { lat: 36.65139, lng: 138.18111 },
  岐阜県: { lat: 35.39111, lng: 136.72222 },
  静岡県: { lat: 34.97694, lng: 138.38306 },
  愛知県: { lat: 35.18028, lng: 136.90667 },
  三重県: { lat: 34.73028, lng: 136.50861 },
  滋賀県: { lat: 35.00444, lng: 135.86833 },
  京都府: { lat: 35.02139, lng: 135.75556 },
  大阪府: { lat: 34.68639, lng: 135.52 },
  兵庫県: { lat: 34.69139, lng: 135.18306 },
  奈良県: { lat: 34.68528, lng: 135.83278 },
  和歌山県: { lat: 34.22611, lng: 135.1675 },
  鳥取県: { lat: 35.50361, lng: 134.23833 },
  島根県: { lat: 35.47222, lng: 133.05056 },
  岡山県: { lat: 34.66167, lng: 133.935 },
  広島県: { lat: 34.39639, lng: 132.45944 },
  山口県: { lat: 34.18583, lng: 131.47139 },
  徳島県: { lat: 34.06583, lng: 134.55944 },
  香川県: { lat: 34.34028, lng: 134.04333 },
  愛媛県: { lat: 33.84167, lng: 132.76611 },
  高知県: { lat: 33.55972, lng: 133.53111 },
  福岡県: { lat: 33.60639, lng: 130.41806 },
  佐賀県: { lat: 33.24944, lng: 130.29889 },
  長崎県: { lat: 32.74472, lng: 129.87361 },
  熊本県: { lat: 32.78972, lng: 130.74167 },
  大分県: { lat: 33.23806, lng: 131.6125 },
  宮崎県: { lat: 31.91111, lng: 131.42389 },
  鹿児島県: { lat: 31.56028, lng: 130.55806 },
  沖縄県: { lat: 26.2125, lng: 127.68111 },
};

const containerStyle = { width: "100%", height: "100%" };

// JP 初期値
const INITIAL_CENTER_JP = { lat: 37.5, lng: 137.0 };
const INITIAL_ZOOM_JP = 5.3;

// 将来：世界表示用（必要になったら scope="world" を渡す）
const INITIAL_CENTER_WORLD = { lat: 20, lng: 0 };
const INITIAL_ZOOM_WORLD = 2.2;

const DARK_MAP_STYLES = [
  { elementType: "geometry", stylers: [{ color: "#020617" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#e5e7eb" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#020617" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#02081a" }] },
  { featureType: "landscape", elementType: "geometry", stylers: [{ color: "#020b2e" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#1f2937" }] },
  { featureType: "road", elementType: "labels.icon", stylers: [{ visibility: "off" }] },
  { featureType: "poi", stylers: [{ visibility: "off" }] },
];

const LIGHT_MAP_STYLES = [{ featureType: "poi", stylers: [{ visibility: "off" }] }];

/**
 * MusicPrefectureMap
 * - デフォルトOFF：中央の「光の柱表示」ボタンのみ
 * - 押した時だけ GoogleMap を mount（= onLoad がその時だけ走る）
 * - `useJsApiLoader` も Inner 側に置いて、押した時だけ読み込む
 */
export default function MusicPrefectureMap({
  prefectureCounts = {},
  accentColor = "#22c55e",
  darkMode = false,
  lowPower = false,
  disablePulse = false,

  // Gate UI
  defaultOpen = false,
  buttonLabel = "光の柱表示",
  closeLabel = "閉じる",

  // 将来拡張：世界表示にする時に "world"
  scope = "jp", // "jp" | "world"
}) {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

  const [open, setOpen] = useState(!!defaultOpen);
  const [shouldLoad, setShouldLoad] = useState(!!defaultOpen);

  const handleOpen = useCallback(() => {
    setOpen(true);
    setShouldLoad(true);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
    // shouldLoad は true のまま保持（同ページ内の再表示で loader 再初期化を避ける）
  }, []);

  if (!apiKey) {
    console.warn("REACT_APP_GOOGLE_MAPS_API_KEY が設定されていません。");
    return null;
  }

  // デフォルトOFF：中央ボタンのみ
  if (!open) {
    return (
      <div style={centerGateWrap(darkMode)}>
        <button type="button" onClick={handleOpen} style={centerPrimaryBtnStyle()}>
          {buttonLabel}
        </button>
      </div>
    );
  }

  // open=true のときだけ Map 本体を mount
  return (
    <div style={{ width: "100%", height: "100%", position: "relative" }}>
      <div style={{ position: "absolute", left: 16, top: 16, zIndex: 5, display: "flex", gap: 10 }}>
        <button type="button" onClick={handleClose} style={ghostBtnStyle(darkMode)}>
          {closeLabel}
        </button>
      </div>

      {shouldLoad ? (
        <MusicPrefectureMapInner
          prefectureCounts={prefectureCounts}
          accentColor={accentColor}
          darkMode={darkMode}
          lowPower={lowPower}
          disablePulse={disablePulse}
          scope={scope}
          apiKey={apiKey}
        />
      ) : null}
    </div>
  );
}

function MusicPrefectureMapInner({
  prefectureCounts,
  accentColor,
  darkMode,
  lowPower,
  disablePulse,
  scope,
  apiKey,
}) {
  // ★ここに置くことで「表示した時だけ」Maps JS をロード
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: apiKey,
    language: scope === "jp" ? "ja" : "en",
    region: scope === "jp" ? "JP" : undefined,
  });

  const mapRef = useRef(null);

  const initialCenter = scope === "world" ? INITIAL_CENTER_WORLD : INITIAL_CENTER_JP;
  const initialZoom = scope === "world" ? INITIAL_ZOOM_WORLD : INITIAL_ZOOM_JP;

  const [zoom, setZoom] = useState(initialZoom);

  const circles = useMemo(() => {
    const entries = Object.entries(prefectureCounts || {});
    return entries
      .map(([pref, count]) => {
        const coord = PREF_COORDS[pref];
        if (!coord) return null;
        return { ...coord, pref, count: Number(count) || 0 };
      })
      .filter(Boolean)
      .filter((c) => c.count > 0);
  }, [prefectureCounts]);

  const [pulse, setPulse] = useState(0);
  useEffect(() => {
    if (disablePulse) return;
    const id = setInterval(() => {
      setPulse((prev) => (prev + 0.18) % (Math.PI * 2));
    }, 120);
    return () => clearInterval(id);
  }, [disablePulse]);

  const pulseFactor = useMemo(() => {
    if (disablePulse) return 1;
    return 1 + 0.18 * Math.sin(pulse);
  }, [pulse, disablePulse]);

  const mapOptions = useMemo(() => {
    return {
      disableDefaultUI: true,
      zoomControl: false,
      fullscreenControl: false,
      draggable: true,
      scrollwheel: true,
      gestureHandling: "greedy",
      mapTypeId: "roadmap",
      styles: darkMode ? DARK_MAP_STYLES : LIGHT_MAP_STYLES,
      clickableIcons: false,
      keyboardShortcuts: false,
      disableDoubleClickZoom: true,
    };
  }, [darkMode]);

  const handleLoad = (map) => {
    mapRef.current = map;
    map.setCenter(initialCenter);
    map.setZoom(initialZoom);
    setZoom(initialZoom);
  };

  const handleZoomIn = () => {
    if (!mapRef.current) return;
    const next = Math.min(12, (mapRef.current.getZoom?.() ?? zoom) + 1);
    mapRef.current.setZoom(next);
    setZoom(next);
  };

  const handleZoomOut = () => {
    if (!mapRef.current) return;
    const next = Math.max(scope === "world" ? 1 : 4, (mapRef.current.getZoom?.() ?? zoom) - 1);
    mapRef.current.setZoom(next);
    setZoom(next);
  };

  const handleFullScreen = () => {
    const el = mapRef.current?.getDiv?.();
    if (!el) return;
    if (!document.fullscreenElement) el.requestFullscreen().catch(() => {});
    else document.exitFullscreen().catch(() => {});
  };

  if (!isLoaded) {
    return (
      <div style={loadingWrap(darkMode)}>
        <div style={loadingText(darkMode)}>読み込み中…</div>
      </div>
    );
  }

  return (
    <GoogleMap
      mapContainerStyle={containerStyle}
      center={initialCenter}
      zoom={zoom}
      onLoad={handleLoad}
      options={mapOptions}
    >
      {circles.map((c) => {
        const baseRadius = lowPower ? 48000 : 55000;
        const radius = baseRadius * pulseFactor;

        return (
          <React.Fragment key={c.pref}>
            <CircleF
              center={{ lat: c.lat, lng: c.lng }}
              radius={radius}
              options={{
                strokeColor: accentColor,
                strokeOpacity: 1,
                strokeWeight: lowPower ? 1.2 : 1.8,
                fillColor: accentColor,
                fillOpacity: lowPower ? 0.22 : 0.3,
              }}
            />
            <CircleF
              center={{ lat: c.lat, lng: c.lng }}
              radius={radius * 0.3}
              options={{
                strokeColor: "#ffffff",
                strokeOpacity: 1,
                strokeWeight: 1.4,
                fillColor: "#ffffff",
                fillOpacity: 1,
              }}
            />
          </React.Fragment>
        );
      })}

      <div style={{ position: "absolute", right: 16, bottom: 18, display: "flex", flexDirection: "column", gap: 10 }}>
        <button type="button" onClick={handleZoomIn} style={btnStyle(22)}>+</button>
        <button type="button" onClick={handleZoomOut} style={btnStyle(22)}>−</button>
        <button type="button" onClick={handleFullScreen} style={btnStyle(18)}>⛶</button>
      </div>
    </GoogleMap>
  );
}

const btnStyle = (fontSize) => ({
  width: 40,
  height: 40,
  borderRadius: 999,
  border: "none",
  background: "rgba(15,23,42,0.9)",
  color: "#fff",
  cursor: "pointer",
  fontSize,
  lineHeight: "40px",
  boxShadow: "0 4px 12px rgba(15,23,42,0.4)",
});

const centerGateWrap = (darkMode) => ({
  width: "100%",
  height: "100%",
  minHeight: 240,
  borderRadius: 16,
  position: "relative",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: darkMode ? "rgba(2,6,23,0.55)" : "rgba(15,23,42,0.06)",
  border: darkMode ? "1px solid rgba(148,163,184,0.18)" : "1px solid rgba(15,23,42,0.12)",
});

const centerPrimaryBtnStyle = () => ({
  padding: "14px 18px",
  borderRadius: 999,
  border: "none",
  cursor: "pointer",
  background: "rgba(34,197,94,0.95)",
  color: "#052e16",
  fontWeight: 900,
  fontSize: 14,
  letterSpacing: 0.2,
  boxShadow: "0 18px 34px rgba(34,197,94,0.22)",
});

const ghostBtnStyle = (darkMode) => ({
  padding: "8px 12px",
  borderRadius: 999,
  border: darkMode ? "1px solid rgba(226,232,240,0.25)" : "1px solid rgba(15,23,42,0.18)",
  background: darkMode ? "rgba(2,6,23,0.35)" : "rgba(255,255,255,0.9)",
  color: darkMode ? "#e5e7eb" : "#0f172a",
  cursor: "pointer",
  fontWeight: 800,
});

const loadingWrap = (darkMode) => ({
  width: "100%",
  height: "100%",
  minHeight: 240,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: darkMode ? "rgba(2,6,23,0.55)" : "rgba(15,23,42,0.06)",
  borderRadius: 16,
});

const loadingText = (darkMode) => ({
  fontSize: 13,
  fontWeight: 800,
  color: darkMode ? "rgba(226,232,240,0.9)" : "rgba(15,23,42,0.75)",
});
